import NoData from '@rio-cloud/rio-uikit/NoData';
import { SCREEN_SM, MOBILE_SCREEN } from '@rio-cloud/rio-uikit/DeviceUtils';
import type { SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import type { TableRowId } from '@rio-cloud/rio-uikit/Table';
import { FormattedMessage } from 'react-intl';
import { useEffect, useRef } from 'react';
import type { Tenant } from '../../../../../configuration/types';
import { config } from '../../../../../config';
import type { Driver, User } from '../../../../../types';
import { PROP_DRIVER_ID } from '../../../schema';
import { DriverMultiSelectContainer } from '../containers/DriverMultiSelectContainer';
import type { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { DriverStatusHint } from './DriverStatusHint';
import { Email } from './Email';
import { Link } from './Link';
import { Telephone } from './Telephone';
import { DriverTagsInRowContainer } from '../containers/DriverTagsInRowContainer';
import Tag from '@rio-cloud/rio-uikit/Tag';
import TagList from '@rio-cloud/rio-uikit/TagList';
import { formatUserNameForDisplay } from '../../../utils';
import { DRIVER_TABLE_COLUMN_KEYS, type DriverTableColumnConfig } from './driverTableColumns';

const SEARCH_HIGHLIGHT_CLASS = 'search-result-highlight';
const SEARCH_IGNORE_SELECTOR = '[data-search-ignore="true"]';

const isSearchHighlightSupported = () =>
    typeof window !== 'undefined' && 'CSS' in window && 'highlights' in CSS && typeof Highlight !== 'undefined';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const collectSearchHighlightRanges = (root: HTMLElement, query: string): Range[] => {
    const ranges: Range[] = [];

    if (!query) {
        return ranges;
    }

    const regex = new RegExp(escapeRegExp(query), 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
            const parentElement = node.parentElement;

            if (!parentElement || parentElement.closest(SEARCH_IGNORE_SELECTOR)) {
                return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
        },
    });

    let node: Node | null = walker.nextNode();

    while (node) {
        const text = node.textContent ?? '';
        regex.lastIndex = 0;

        let match: RegExpExecArray | null = regex.exec(text);

        while (match) {
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            ranges.push(range);

            match = regex.exec(text);
        }

        node = walker.nextNode();
    }

    return ranges;
};

const renderNoData = () => (
    <span data-search-ignore='true'>
        <NoData text={<FormattedMessage id='intl-msg:common-message.noData' defaultMessage='No data' />} />
    </span>
);

export interface DriversTableProps {
    columns: DriverTableColumnConfig[];
    compactRows: boolean;
    drivers: Driver[];
    users: User[];
    gridLines: boolean;
    selectedDriverId: string | null;
    driversListMode: TableViewTogglesViewType;
    selectedDriverIdsForMultiSelect: string[];
    toggleAllCheckboxes: (enable: boolean) => void;
    onSelectionChange: (rowIds: string[]) => void;
    onActiveRowChange: (rowId: string | undefined) => void;
    onRowClick: (rowId: string) => void;
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnResize: (columnKey: string, width?: number | string) => void;
    onSortChange: (sortBy: string, sortDir: SortDirectionType) => void;
    onViewTypeChange: (mode: TableViewTogglesViewType) => void;
    sortBy: string;
    sortDir: SortDirectionType;
    searchText: string;
    tableResetKey: number;
    tenant: Tenant | null;
}

export const DriversTable = (props: DriversTableProps) => {
    const {
        columns,
        compactRows,
        drivers,
        users,
        gridLines,
        selectedDriverId,
        driversListMode,
        selectedDriverIdsForMultiSelect,
        toggleAllCheckboxes,
        onSelectionChange,
        onActiveRowChange,
        onRowClick,
        onColumnOrderChange,
        onColumnResize,
        onSortChange,
        onViewTypeChange,
        sortBy,
        sortDir,
        searchText,
        tableResetKey,
    } = props;
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const visibleColumns = columns.filter(column => column.visible);
    const visibleColumnStructureKey = visibleColumns.map(column => column.key).join('-');
    const resolvedSortBy =
        sortBy === DRIVER_TABLE_COLUMN_KEYS.FIRST_NAME || sortBy === DRIVER_TABLE_COLUMN_KEYS.LAST_NAME
            ? DRIVER_TABLE_COLUMN_KEYS.DRIVER
            : sortBy;

    useEffect(() => {
        if (!isSearchHighlightSupported()) {
            return;
        }

        const tableBody = tableContainerRef.current?.querySelector('.table-body') as HTMLElement | null;

        if (!tableBody) {
            return;
        }

        const highlight = CSS.highlights.get(SEARCH_HIGHLIGHT_CLASS) ?? new Highlight();
        CSS.highlights.set(SEARCH_HIGHLIGHT_CLASS, highlight);

        const query = searchText.trim();
        const raf = window.requestAnimationFrame(() => {
            highlight.clear();

            for (const range of collectSearchHighlightRanges(tableBody, query)) {
                highlight.add(range);
            }
        });

        return () => {
            window.cancelAnimationFrame(raf);
            highlight.clear();
        };
    }, [searchText, drivers, users, visibleColumns]);

    const renderDriverColumn = (driver: Driver, user: User | undefined) => {
        const driverName = [driver.firstName?.trim(), driver.lastName?.trim()].filter(Boolean).join(' ');
        const userName = formatUserNameForDisplay(user).trim();

        if (!driverName && !userName) {
            return renderNoData();
        }

        return (
            <div className='display-flex align-items-center gap-10'>
                <span className='uikit-icon icon-driver text-size-h1' />
                <div className='display-flex flex-column min-width-0'>
                    <div className='ellipsis-1 line-height-125rel text-medium' title={driverName || undefined}>
                        {driverName || (
                            <FormattedMessage id='intl-msg:common-message.noData' defaultMessage='No data' />
                        )}
                    </div>
                    {userName ? (
                        <div className='ellipsis-1 line-height-125rel text-color-gray' title={userName}>
                            {userName}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    const renderColumn = (driver: Driver, columnKey: DriverTableColumnConfig['key']) => {
        const user = users.find(userEntry => userEntry.subject === driver.subject);

        switch (columnKey) {
            case 'driver':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {renderDriverColumn(driver, user)}
                    </TableColumn>
                );
            case 'status':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey}>
                        <DriverStatusHint driverStatus={driver.status} />
                    </TableColumn>
                );
            case 'firstName':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {driver.firstName?.trim() ? driver.firstName : renderNoData()}
                    </TableColumn>
                );
            case 'lastName':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {driver.lastName?.trim() ? driver.lastName : renderNoData()}
                    </TableColumn>
                );
            case 'card':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='visible'>
                        {driver.identifications.length ? (
                            <TagList>
                                <div>
                                    {driver.identifications
                                        .slice()
                                        .sort((left, right) => left.value.localeCompare(right.value))
                                        .map(identification => (
                                            <Tag
                                                key={identification.id}
                                                className='white-space-nowrap margin-1'
                                                size='small'
                                            >
                                                {identification.value}
                                            </Tag>
                                        ))}
                                </div>
                            </TagList>
                        ) : (
                            renderNoData()
                        )}
                    </TableColumn>
                );
            case 'email':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {driver.email?.trim() ? <Email email={driver.email} /> : renderNoData()}
                    </TableColumn>
                );
            case 'phoneNumber':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {driver.phoneNumber?.trim() ? <Telephone phoneNumber={driver.phoneNumber} /> : renderNoData()}
                    </TableColumn>
                );
            case 'user':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='ellipsis'>
                        {driver.subject && formatUserNameForDisplay(user).trim() ? (
                            <Link
                                targetUri={`${config.backend.USERADMIN_WEB}/#/users/${user?.id}`}
                                text={formatUserNameForDisplay(user)}
                            />
                        ) : (
                            renderNoData()
                        )}
                    </TableColumn>
                );
            case 'tags':
                return (
                    <TableColumn columnKey={columnKey} key={columnKey} overflow='visible'>
                        {driver.tags?.length ? <DriverTagsInRowContainer tags={driver.tags} /> : renderNoData()}
                    </TableColumn>
                );
            default:
                return null;
        }
    };

    return (
        <div ref={tableContainerRef}>
            <Table
                key={`drivers-table-${tableResetKey}-${visibleColumnStructureKey}`}
                activeRowId={selectedDriverId ?? undefined}
                border
                cardsStyle={{ minWidth: 320 }}
                condensed={compactRows}
                gridLines={gridLines}
                hover
                onActiveRowChange={rowId => onActiveRowChange(rowId ? String(rowId) : undefined)}
                onColumnOrderChange={onColumnOrderChange}
                onColumnResize={onColumnResize}
                onRowClick={row => onRowClick(String(row[PROP_DRIVER_ID]))}
                onSelectionChange={rowIds => onSelectionChange(rowIds.map(rowId => String(rowId)))}
                onSortChange={(columnKey, direction) =>
                    onSortChange(
                        columnKey === DRIVER_TABLE_COLUMN_KEYS.DRIVER ? DRIVER_TABLE_COLUMN_KEYS.LAST_NAME : columnKey,
                        direction
                    )
                }
                onViewTypeChange={onViewTypeChange}
                responsiveBreakpoint={{
                    cards: `${SCREEN_SM}px`,
                    list: `${MOBILE_SCREEN}px`,
                }}
                rowKey={PROP_DRIVER_ID}
                selectedRowIds={selectedDriverIdsForMultiSelect}
                selectionHeaderContent={
                    <DriverMultiSelectContainer
                        selectedDriverIds={selectedDriverIdsForMultiSelect}
                        toggleAllCheckboxes={toggleAllCheckboxes}
                    />
                }
                showSelectionColumn
                sortBy={resolvedSortBy}
                sortDirection={sortDir}
                viewType={driversListMode}
            >
                <TableHeader>
                    {visibleColumns.map(column => (
                        <TableHeaderColumn
                            key={column.key}
                            columnKey={column.key}
                            draggable={column.draggable}
                            hideLabel={column.hideLabel}
                            horizontalAlign={column.horizontalAlign}
                            maxResizeWidth={column.maxResizeWidth}
                            mobileLabel={column.mobileLabel}
                            minResizeWidth={column.minResizeWidth}
                            resizeable={column.resizeable}
                            sortable={column.sortable}
                            width={column.width}
                        >
                            {column.label}
                        </TableHeaderColumn>
                    ))}
                </TableHeader>
                <TableBody data-testid='driverTableBody'>
                    {drivers.map(driver => {
                        return (
                            <TableRow
                                {...driver}
                                data-selected={
                                    selectedDriverIdsForMultiSelect.includes(driver.driverId) ? 'true' : 'false'
                                }
                                data-testid='driverRow'
                                id={driver.driverId as TableRowId}
                                key={driver[PROP_DRIVER_ID]}
                            >
                                {visibleColumns.map(column => renderColumn(driver, column.key))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};
